import { Field, FieldInput, FieldLabel } from '@ibe/ui';
import { useId, useState } from 'react';
import { z } from 'zod';

import { useApiQuery } from '../../lib/api/hooks.js';
import { queryKeys } from '../../lib/api/query-keys.js';

const optionSchema = z.array(z.object({ value: z.string(), label: z.string() }));

export interface PropertyOption {
  readonly value: string;
  readonly label: string;
}

export interface PropertyMultiSelectProps {
  readonly value: readonly string[];
  readonly onChange: (value: readonly string[]) => void;
  readonly error?: string | undefined;
  readonly required?: boolean;
  readonly label?: string;
  readonly description?: string;
  readonly options?: readonly PropertyOption[];
  readonly isLoading?: boolean | undefined;
  readonly searchPlaceholder?: string;
  readonly loadingMessage?: string;
  readonly emptyMessage?: string;
}

export function PropertyMultiSelect({
  value,
  onChange,
  error,
  required = false,
  label = 'Properties',
  description = 'Choose the properties in this group.',
  options: optionsProp,
  isLoading: isLoadingProp,
  searchPlaceholder = 'Search properties…',
  loadingMessage = 'Loading properties…',
  emptyMessage = 'No properties match.',
}: PropertyMultiSelectProps): React.JSX.Element {
  const [search, setSearch] = useState('');

  const query = useApiQuery({
    queryKey: queryKeys.properties.options(search),
    path: '/v1/properties/options',
    schema: optionSchema,
    query: { q: search, limit: '50' },
    enabled: optionsProp === undefined,
  });

  const loadedOptions = optionsProp ?? query.data ?? [];
  const byId = new Map(loadedOptions.map((option) => [option.value, option.label]));
  const allOptions: PropertyOption[] = [...loadedOptions];
  for (const propertyId of value) {
    if (!byId.has(propertyId)) {
      allOptions.push({ value: propertyId, label: `Property ${propertyId}` });
    }
  }

  const needle = search.trim().toLowerCase();
  const options =
    needle === ''
      ? allOptions
      : allOptions.filter(
          (option) =>
            option.label.toLowerCase().includes(needle) ||
            option.value.toLowerCase().includes(needle),
        );

  const toggle = (propertyId: string): void => {
    const selected = new Set(value);
    if (selected.has(propertyId)) {
      selected.delete(propertyId);
    } else {
      selected.add(propertyId);
    }
    onChange([...selected]);
  };

  const showLabel = label !== '';
  const isLoading = isLoadingProp ?? (optionsProp === undefined && query.isPending);

  return (
    <Field error={error} required={required} description={description}>
      {showLabel ? <FieldLabel>{label}</FieldLabel> : null}
      <FieldInput
        autoComplete="off"
        aria-label={showLabel ? undefined : `Search ${label.toLowerCase()}`}
        placeholder={searchPlaceholder}
        value={search}
        onChange={(event) => {
          setSearch(event.target.value);
        }}
      />
      <div className="max-h-60 overflow-y-auto rounded-md border border-input p-2">
        {isLoading ? (
          <p className="text-sm text-muted-foreground">{loadingMessage}</p>
        ) : options.length === 0 ? (
          <p className="text-sm text-muted-foreground">{emptyMessage}</p>
        ) : (
          options.map((option) => {
            const checked = value.includes(option.value);
            return (
              <PropertyCheckbox
                key={option.value}
                label={option.label}
                checked={checked}
                onChange={() => {
                  toggle(option.value);
                }}
              />
            );
          })
        )}
      </div>
    </Field>
  );
}

function PropertyCheckbox({
  label,
  checked,
  onChange,
}: {
  readonly label: string;
  readonly checked: boolean;
  readonly onChange: () => void;
}): React.JSX.Element {
  const inputId = useId();
  return (
    <div className="flex items-center gap-2 py-1">
      <input
        id={inputId}
        type="checkbox"
        checked={checked}
        onChange={onChange}
        className="h-4 w-4 rounded-sm border border-input"
      />
      <label htmlFor={inputId} className="text-sm leading-none">
        {label}
      </label>
    </div>
  );
}
